import React from 'react';
import dayjs from 'dayjs';

/**
 * Sales-page date-range filter, expressed as ISO `YYYY-MM-DD` strings.
 * The format is deliberately serialization-friendly — it can be sent
 * straight to the API's `From` / `To` params and survives URL-sync if
 * we add that later — while being trivial to convert to dayjs or
 * `@internationalized/date` `CalendarDate` at any consumer boundary.
 */
export type SalesDateRange = {
    start: string;
    end: string;
};

export type SalesContextValue = {
    dateRange: SalesDateRange;
    setDateRange: (range: SalesDateRange) => void;
};

const SalesContext = React.createContext<SalesContextValue | null>(null);

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Returns the inclusive ISO Monday–Sunday range that the current
 * calendar day falls in, in the browser's local time zone. Used as the
 * provider's seed value so the page lands on "this week" the first
 * time it mounts.
 */
const getCurrentIsoWeek = (): SalesDateRange => {
    // dayjs `.day()` is Sun=0 … Sat=6. ISO week starts on Monday, so
    // walk back `(day + 6) % 7` days. Manual rather than `.startOf('week')`
    // because that helper is locale-sensitive and we want Mon-first
    // unconditionally.
    const today = dayjs();
    const daysSinceMonday = (today.day() + 6) % 7;
    const monday = today.subtract(daysSinceMonday, 'day');
    const sunday = monday.add(6, 'day');
    return {
        start: monday.format(ISO_DATE_FORMAT),
        end: sunday.format(ISO_DATE_FORMAT),
    };
};

export const SalesProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [dateRange, setDateRange] =
        React.useState<SalesDateRange>(getCurrentIsoWeek);

    const value = React.useMemo<SalesContextValue>(
        () => ({ dateRange, setDateRange }),
        [dateRange],
    );

    return (
        <SalesContext.Provider value={value}>{children}</SalesContext.Provider>
    );
};

/**
 * Reads the Sales page's shared filter state. Must be called inside a
 * `SalesProvider`; throws otherwise so misuses are caught at render
 * time rather than producing a silently empty range.
 */
export const useSales = (): SalesContextValue => {
    const value = React.useContext(SalesContext);
    if (value === null) {
        throw new Error('useSales must be used within a <SalesProvider>');
    }
    return value;
};
